// The ring the Solar System view draws round whatever the cursor is on.
//
// Placed and sized in ANGULAR terms by the caller, because out at the
// heliocentric view every body is drawn at one floored angular size and a halo
// scaled off a radius would be a pixel wide on some of them and fill the pane
// on others. What the ring is drawn round is the caller's business; this knows
// only a point, a size and whether it is on the pane at all.

import * as THREE from 'three';

import type { WorldFrameHandle, WorldVec } from '../world/frame.ts';
import { inkToneMapped } from './orrery.ts';

/**
 * The colour the rest of the view already uses for a thing you can act on.
 *
 * #8ee9ff as it drew at the SYSTEM band, exposure 1.25, through ACES: the
 * halo is ink like the orrery's own lines, so it is drawn untone-mapped at
 * the colour it used to be metered to (`inkToneMapped` in `orrery.ts`).
 */
export const HOVER_HALO_CSS = '#c4e3e8';

const DEG = Math.PI / 180;
const RING_PX = 128;
const RIM_PX = 256;

/**
 * Where the ring is drawn in its own texture, as a fraction of the half-width.
 *
 * The caller sizes the sprite, so it is the only thing that can know whether
 * the ring lands clear of the body it is round: the stroke sits at 52 of the
 * 64 px half-width, so a halo asked for at 20 degrees draws its ring at 16.25.
 */
export const HOVER_HALO_RING_FRACTION = 52 / (RING_PX / 2);

/** And where the RIM's inner edge is, which the caller puts on the limb. */
export const HOVER_HALO_RIM_FRACTION = 96 / (RIM_PX / 2);

/**
 * A ring over a soft glow, drawn once into a texture.
 *
 * The glow and not the ring alone, because at the heliocentric rungs the whole
 * halo is thirty pixels across and a ring alone is a two-pixel outline: what
 * says "this is the one the click takes" there is the wash inside it.
 */
function ringTexture(): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = RING_PX;
  c.height = RING_PX;
  const g = c.getContext('2d');
  if (g) {
    const m = RING_PX / 2;
    g.clearRect(0, 0, RING_PX, RING_PX);
    const glow = g.createRadialGradient(m, m, 0, m, m, 60);
    glow.addColorStop(0, 'rgba(196,227,232,0.30)');
    glow.addColorStop(0.78, 'rgba(196,227,232,0.34)');
    glow.addColorStop(1, 'rgba(196,227,232,0)');
    g.fillStyle = glow;
    g.beginPath();
    g.arc(m, m, 60, 0, Math.PI * 2);
    g.fill();
    g.strokeStyle = HOVER_HALO_CSS;
    g.lineWidth = 9;
    g.beginPath();
    g.arc(m, m, 52, 0, Math.PI * 2);
    g.stroke();
  }
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

/**
 * The same answer for a body drawn as a disc: a rim on its limb.
 *
 * Nothing inside it. The ring's own wash is what says "this dot is the one the
 * click takes" when the dot is two pixels across, and the same wash over a
 * planet that fills the frame is a blue veil over the thing it is pointing at.
 * The lit band starts at the inner edge and falls off OUTWARD, so the caller
 * placing that edge on the limb puts every lit pixel at or outside it.
 */
function rimTexture(): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = RIM_PX;
  c.height = RIM_PX;
  const g = c.getContext('2d');
  if (g) {
    const m = RIM_PX / 2;
    const inner = 96;
    g.clearRect(0, 0, RIM_PX, RIM_PX);
    const glow = g.createRadialGradient(m, m, inner, m, m, 124);
    glow.addColorStop(0, 'rgba(196,227,232,0.34)');
    glow.addColorStop(0.35, 'rgba(196,227,232,0.18)');
    glow.addColorStop(1, 'rgba(196,227,232,0)');
    g.fillStyle = glow;
    g.beginPath();
    g.arc(m, m, 124, 0, Math.PI * 2);
    g.arc(m, m, inner, 0, Math.PI * 2, true);
    g.fill('evenodd');
    g.strokeStyle = HOVER_HALO_CSS;
    g.lineWidth = 8;
    g.beginPath();
    g.arc(m, m, inner + 4, 0, Math.PI * 2);
    g.stroke();
  }
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

export interface HoverHaloConfig {
  /** The camera-relative frame the orrery's own bodies are registered in. */
  frame: WorldFrameHandle;
}

export interface HoverHaloHandle {
  readonly object: THREE.Object3D;
  readonly visible: boolean;
  /** What the ring subtends this frame, or 0 while it is not drawn. */
  readonly drawnAngularDiameterDeg: number;
  /** Which of the two the last `show` drew. */
  readonly rim: boolean;
  /**
   * Put the halo on a point at an angular size, or `null` to take it off.
   *
   * `rim` swaps the ring and its wash for a band on the sprite's own edge; the
   * size is the sprite's either way, and `HOVER_HALO_RING_FRACTION` /
   * `HOVER_HALO_RIM_FRACTION` say where inside it each is drawn.
   */
  show(at: WorldVec | null, angularDiameterDeg: number, cameraWorld: WorldVec,
    rim: boolean): boolean;
  dispose(): void;
}

export function createHoverHalo(config: HoverHaloConfig): HoverHaloHandle {
  const texture = ringTexture();
  const rimMap = rimTexture();
  const material = new THREE.SpriteMaterial({
    map: texture,
    transparent: true,
    toneMapped: inkToneMapped(),
    // The ring answers the cursor, so whatever the cursor is over cannot be
    // allowed to hide its own answer.
    depthTest: false,
    depthWrite: false,
    opacity: 0.9,
  });
  const sprite = new THREE.Sprite(material);
  sprite.name = 'atlas-hover-halo';
  sprite.renderOrder = 29;
  sprite.visible = false;

  const world: WorldVec = { x: 0, y: 0, z: 0 };
  let drawnDeg = 0;
  let drawnRim = false;

  const unregister = config.frame.register(sprite, () => world);

  return {
    object: sprite,
    get visible(): boolean { return sprite.visible; },
    get drawnAngularDiameterDeg(): number { return drawnDeg; },
    get rim(): boolean { return drawnRim; },
    show(at: WorldVec | null, angularDiameterDeg: number, cameraWorld: WorldVec,
      rim: boolean): boolean {
      const on = at !== null && angularDiameterDeg > 0;
      let changed = sprite.visible !== on;
      sprite.visible = on;
      if (!on || at === null) { drawnDeg = 0; return changed; }
      if (rim !== drawnRim) {
        drawnRim = rim;
        material.map = rim ? rimMap : texture;
        material.needsUpdate = true;
        changed = true;
      }
      world.x = at.x;
      world.y = at.y;
      world.z = at.z;
      const d = Math.hypot(at.x - cameraWorld.x, at.y - cameraWorld.y, at.z - cameraWorld.z);
      const span = 2 * Math.tan(angularDiameterDeg * 0.5 * DEG) * Math.max(1, d);
      sprite.scale.set(span, span, 1);
      drawnDeg = angularDiameterDeg;
      return changed;
    },
    dispose(): void {
      unregister();
      material.dispose();
      texture.dispose();
      rimMap.dispose();
    },
  };
}
